/** Loaders paginados dos catálogos grandes/dinâmicos do sistema. */
import { createElement } from 'react'
import type {
  Asset,
  Cliente,
  ConjuntoCaminhao,
  ConjuntoMaquinario,
  Contract,
  Dupla,
  Fleet,
  Frente,
  Paginated,
  Personnel,
  Place,
  Subcontrato,
  Usuario,
} from '../../core/api/types'
import { http } from '../../core/api/client'
import { FleetIcon } from '../components/FleetIcon'
import { CARRETA, CARROCERIAS_PARAM, MAQUINA, PLATAFORMA, UTILITARIO } from '../fleetTipos'
import { createRemoteOptionsLoader } from './remoteOptions'
import type { SelectOptionsLoader } from './SearchableSelect'
import { currentCategoryLabel } from '../categoryLabels'

const fleetLabel = (item: Fleet) => item.plate || item.code || item.name || item.uuid

/** As duas visões do cliente em toda escolha de Cliente do sistema: em cima a
 *  FILIAL, o nome fantasia com que a operação fala (MONSANTO CAMPO VERDE); em
 *  baixo, discreta, a EMPRESA PAI, a razão social que agrupa as filiais
 *  (MONSANTO DO BRASIL LTDA). Cliente sem fantasia própria não repete o nome. */
export const empresaPaiDoCliente = (item: Pick<Cliente, 'legal_name' | 'trade_name'>): string | undefined => (
  item.trade_name && item.trade_name !== item.legal_name ? item.legal_name : undefined
)

export const customerOptionsLoader = createRemoteOptionsLoader<Cliente>(
  '/customers',
  (item) => ({
    value: item.uuid,
    label: item.trade_name || item.legal_name,
    sublabel: empresaPaiDoCliente(item),
    keywords: [item.legal_name, item.cnpj ?? ''],
  }),
)

export const farmOptionsLoader = createRemoteOptionsLoader<Place>(
  '/places',
  (item) => ({ value: item.uuid, label: item.name, keywords: [item.code ?? '', item.city ?? '', item.uf ?? ''] }),
  { category: 'farm' },
)

/** As categorias de Local que são ponta de viagem: o cadastro inteiro menos hotel,
 *  que é hospedagem do maquinário e não origem nem destino de carga. */
export const CATEGORIAS_DE_PONTA_DE_VIAGEM = 'farm,unit,warehouse,headquarters'

/** Origem e destino de Outros Serviços Transporte (01/10/2026): só local cadastrado e
 *  ativo, sem digitação livre, para o mesmo endereço não nascer com dois nomes. */
export const pontaDeViagemOptionsLoader = createRemoteOptionsLoader<Place>(
  '/places',
  (item) => ({
    value: item.uuid,
    label: item.name,
    sublabel: [item.city, item.uf].filter(Boolean).join('/') || undefined,
    keywords: [item.code ?? '', item.city ?? '', item.uf ?? ''],
  }),
  { category: CATEGORIAS_DE_PONTA_DE_VIAGEM, status: 'active' },
)

export const unitOptionsLoader = createRemoteOptionsLoader<Place>(
  '/places',
  (item) => ({ value: item.uuid, label: item.name, keywords: [item.code ?? '', item.city ?? '', item.uf ?? ''] }),
  { category: 'unit' },
)

export const activeUnitOptionsLoader = createRemoteOptionsLoader<Place>(
  '/places',
  (item) => ({
    value: item.uuid,
    label: [item.name, item.code, [item.city, item.uf].filter(Boolean).join('/')].filter(Boolean).join(' · '),
    keywords: [item.name, item.code ?? '', item.city ?? '', item.uf ?? ''],
  }),
  { category: 'unit', status: 'active' },
)

/** Subcontratos do Protheus para o seletor da linha da Frente.
 *
 *  O endpoint só devolve os VIGENTES: cancelado e encerrado já foram baixados pelo
 *  ERP, e oferecê-los seria oferecer carga que não existe mais.
 *
 *  A opção é lida em três degraus, do mais específico ao mais geral: a chave em
 *  cima, porque é por ela que o operador confere contra o ERP; a fazenda e a rota
 *  embaixo, que é como ele reconhece a carga sem decorar código. Sem parêntese e
 *  sem travessão encaixando informação — os dois viram linhas separadas. */
export const subcontratoOptionsLoader = createRemoteOptionsLoader<Subcontrato>(
  '/subcontratos',
  (item) => ({
    value: item.uuid,
    label: item.subcontrato,
    sublabel: [item.descricao, item.rota].filter(Boolean).join(' · ') || undefined,
    hint: item.cliente ?? undefined,
    keywords: [
      item.descricao ?? '', item.cliente ?? '', item.rota ?? '',
      item.lavoura ?? '', item.contrato ?? '',
    ],
  }),
)

/** Lavouras para o seletor de LAVOURA DESTINO do Transporte de Máquina (09/09/2026).
 *
 *  Mesma rota do `subcontratoOptionsLoader`, outro rótulo: ali a operação confere a
 *  chave contra o ERP, aqui ela procura a lavoura, que é a palavra que o Samuel usou.
 *  O valor continua sendo o uuid do subcontrato, porque é dele que saem também a
 *  inscrição estadual e o CPF/CNPJ do cooperante.
 *
 *  A lavoura NÃO identifica sozinha: são 2416 códigos distintos em 2943 linhas, e mil
 *  linhas dividem o código com pelo menos outra. Por isso a fazenda e a rota descem
 *  como segunda linha, e a chave do subcontrato fica à direita. Sem elas o operador
 *  veria duas opções idênticas e escolheria no escuro.
 *
 *  `com_lavoura` recorta a lavoura em branco: opção sem rótulo é inclicável. */
export const lavouraOptionsLoader = createRemoteOptionsLoader<Subcontrato>(
  '/subcontratos',
  (item) => ({
    value: item.uuid,
    label: item.lavoura ?? item.subcontrato,
    sublabel: [item.descricao, item.rota].filter(Boolean).join(' · ') || undefined,
    hint: item.subcontrato,
    keywords: [
      item.subcontrato, item.contrato ?? '', item.descricao ?? '',
      item.cliente ?? '', item.rota ?? '',
    ],
  }),
  { com_lavoura: 1 },
)

export const assetOptionsLoader = createRemoteOptionsLoader<Asset>(
  '/assets',
  (item) => ({ value: item.uuid, label: item.name }),
)

export const duplaOptionsLoader = createRemoteOptionsLoader<Dupla>(
  '/duplas',
  (item) => ({ value: item.uuid, label: item.name, hint: item.code ?? undefined, keywords: [item.code ?? ''] }),
)

/** Frentes do espelho do Protheus, para o vínculo do contrato (virada de safra
 *  01/09/2026). O número identifica sozinho; o apelido e os devedores dos dois
 *  trilhos entram como texto de apoio e busca. */
export const frenteOptionsLoader = createRemoteOptionsLoader<Frente>(
  '/frentes',
  (item) => ({
    value: item.uuid,
    label: item.num_frente,
    sublabel: item.apelido ?? undefined,
    hint: item.status_frente ?? undefined,
    keywords: [
      item.apelido ?? '', item.devedor_tms ?? '', item.devedor_gct ?? '',
      item.contrato_tms ?? '', item.contrato_gct ?? '',
      item.subcontrato_tms ?? '', item.subcontrato_gct ?? '',
    ],
  }),
)

export const personnelOptionsLoader = createRemoteOptionsLoader<Personnel>(
  '/personnel',
  (item) => ({ value: item.uuid, label: item.name, keywords: [item.code ?? '', item.document ?? '', item.filial ?? ''] }),
)

export const driverOptionsLoader = createRemoteOptionsLoader<Personnel>(
  '/personnel',
  (item) => ({ value: item.uuid, label: item.name, keywords: [item.code ?? '', item.document ?? '', item.filial ?? ''] }),
  { category: 'driver' },
)

export const operatorOptionsLoader = createRemoteOptionsLoader<Personnel>(
  '/personnel',
  (item) => ({ value: item.uuid, label: item.name, keywords: [item.code ?? '', item.document ?? '', item.filial ?? ''] }),
  { category: 'operator' },
)

export const fleetOptionsLoader = createRemoteOptionsLoader<Fleet>(
  '/fleet',
  (item) => ({ value: item.uuid, label: fleetLabel(item), keywords: [item.code ?? '', item.name ?? '', item.filial ?? ''] }),
)

/** Placa com o desenho do tipo ao lado, usada nos seletores de frota. */
const fleetOption = (item: Fleet) => ({
  value: item.uuid,
  label: fleetLabel(item),
  hint: item.category ? currentCategoryLabel(item.category) : undefined,
  icon: createElement(FleetIcon, { category: item.category }),
  keywords: [item.code ?? '', item.name ?? '', item.filial ?? '', item.category?.label ?? ''],
})

/** Cavalo: tipo é a composição inteira. Carreta: tipo próprio, desenhada sozinha. */
export const truckOptionsLoader = createRemoteOptionsLoader<Fleet>(
  '/fleet', fleetOption, { category: CARROCERIAS_PARAM },
)

export const trailerOptionsLoader = createRemoteOptionsLoader<Fleet>(
  '/fleet', fleetOption, { category: CARRETA },
)

/** Plataforma: o tipo cadastrado à mão que acompanha a máquina (04/09/2026). */
export const platformOptionsLoader = createRemoteOptionsLoader<Fleet>(
  '/fleet', fleetOption, { category: PLATAFORMA },
)

/** Utilitário: a caminhonete de apoio, para o conjunto de utilitário. */
export const utilityVehicleOptionsLoader = createRemoteOptionsLoader<Fleet>(
  '/fleet', fleetOption, { category: UTILITARIO },
)

/** Hotéis: os locais de categoria hotel, para a hospedagem do maquinário. */
export const hotelOptionsLoader = createRemoteOptionsLoader<Place>(
  '/places',
  (item) => ({ value: item.uuid, label: item.name, sublabel: [item.city, item.uf].filter(Boolean).join('/') || undefined, keywords: [item.code ?? '', item.city ?? ''] }),
  { category: 'hotel', status: 'active' },
)

const machineOption = (item: Fleet) => ({
  value: item.uuid,
  label: item.code || item.plate || item.name || item.uuid,
  hint: item.category ? currentCategoryLabel(item.category) : undefined,
  icon: createElement(FleetIcon, { category: item.category }),
  keywords: [item.name ?? '', item.plate ?? ''],
})

export const machineOptionsLoader = createRemoteOptionsLoader<Fleet>('/fleet', machineOption, { category: MAQUINA })

/** As máquinas que podem entrar no maquinário de UMA frente (Samuel, 15/10/2026): só
 *  as que têm viagem de prancha destinada ao contrato dela no Transporte de Máquina,
 *  a primeira e a segunda máquina da viagem. Mesma opção do seletor geral. */
export const maquinaComPranchaOptionsLoader = (contractUuid: string) => createRemoteOptionsLoader<Fleet>(
  '/contract-maquinario/maquinas-elegiveis', machineOption, { contract: contractUuid },
)

export const staffUserOptionsLoader = createRemoteOptionsLoader<Usuario>(
  '/users',
  (item) => ({ value: item.uuid, label: item.name || item.email, keywords: [item.email] }),
  { scope: 'staff' },
)

export const conjuntoCaminhaoOptionsLoader = createRemoteOptionsLoader<ConjuntoCaminhao>(
  '/conjuntos-caminhao',
  (item) => ({
    value: item.uuid,
    label: item.name ?? item.driver?.name ?? item.uuid,
    keywords: [item.driver?.name ?? '', item.truck?.plate ?? '', item.trailer1?.plate ?? '', item.trailer2?.plate ?? ''],
  }),
)

/** Motoristas para a viagem de prancha, rotulados com a função operacional.
 *
 *  O cliente pediu, em 24/08/2026, que a viagem de prancha seja de PRANCHEIRO. A
 *  regra aparece aqui como SINAL, não como bloqueio, e a razão é de implantação: o
 *  cadastro inteiro nasceu classificado como Motorista, então filtrar de saída
 *  deixaria o seletor vazio e o módulo inteiro sem uso até alguém reclassificar as
 *  pessoas uma a uma. O `hint` mostra a função de cada um, e a opção de quem não é
 *  prancheiro vem com o motivo escrito.
 *
 *  Até 15/09/2026 este seletor listava CONJUNTOS de prancha, e escolher um
 *  sobrescrevia motorista, cavalo e carreta de uma vez. Samuel precisava trocar só
 *  o motorista de uma viagem, como o lápis da Frente faz, então o seletor passou a
 *  listar pessoas e cada célula do grupo Ativos grava só o seu campo. O conjunto do
 *  motorista continua sugerindo cavalo e carreta quando a linha ainda não os tem.
 *
 *  Para fechar de verdade, quando as pessoas estiverem classificadas: acrescentar
 *  `funcao_operacional: 'prancheiro'` ao filtro abaixo. */
export const pranchaDriverOptionsLoader = createRemoteOptionsLoader<Personnel>(
  '/personnel',
  (item) => {
    const funcao = item.funcao_operacional
    const ehPrancheiro = !funcao || funcao === 'prancheiro'
    return {
      value: item.uuid,
      label: item.name,
      hint: item.funcao_operacional_label ?? undefined,
      sublabel: ehPrancheiro ? undefined : 'Função operacional não é Prancheiro',
      keywords: [item.code ?? '', item.document ?? '', item.filial ?? ''],
    }
  },
  { category: 'driver' },
)

/** Cavalos de PRANCHA, para a célula Placa do Transporte de Máquina. Prancha é
 *  carroceria do cavalo, não da carreta: a carreta do conjunto é sempre `trailer`. */
export const pranchaTruckOptionsLoader = createRemoteOptionsLoader<Fleet>(
  '/fleet', fleetOption, { category: 'prancha' },
)

export const conjuntoMaquinarioOptionsLoader = createRemoteOptionsLoader<ConjuntoMaquinario>(
  '/conjuntos-maquinario',
  (item) => ({
    value: item.uuid,
    label: item.name ?? item.machine?.name ?? item.operator?.name ?? item.uuid,
    keywords: [item.machine?.plate ?? '', item.operator?.name ?? ''],
  }),
)

export const machineConjuntoOptionsLoader = createRemoteOptionsLoader<ConjuntoMaquinario>(
  '/conjuntos-maquinario',
  (item) => ({
    value: item.uuid,
    label: item.machine?.name ?? item.machine?.plate ?? item.name ?? item.uuid,
    keywords: [item.machine?.plate ?? '', item.operator?.name ?? ''],
  }),
)

export const operatorConjuntoOptionsLoader = createRemoteOptionsLoader<ConjuntoMaquinario>(
  '/conjuntos-maquinario',
  (item) => ({
    value: item.uuid,
    label: item.operator?.name ?? item.name ?? item.uuid,
    keywords: [item.machine?.name ?? '', item.machine?.plate ?? ''],
  }),
)

export const contractOptionsLoader = createRemoteOptionsLoader<Contract>(
  '/contracts',
  (item) => ({
    value: item.uuid,
    label: `${item.contract_number ?? item.name}${item.producer ? ` — ${item.producer}` : ''}`,
    keywords: [item.name, item.contract_number ?? '', item.producer ?? '', item.customer_nome ?? '', item.farm?.name ?? ''],
  }),
)

/** Origem/destino do Transporte de Máquina: contrato OU garagem (place da categoria
 *  Garagem). O valor vem PREFIXADO (`contract:` ou `garage:`) p/ a página decidir se
 *  grava `*_contract_uuid` ou `*_place_uuid`. Garagens (poucas) vêm na 1ª página, os
 *  contratos seguem paginando.
 *
 *  `todas_duplas` desliga o recorte por dupla da lista de contratos (Samuel,
 *  17/09/2026). É a única lista do sistema que o faz, e o motivo é a própria viagem:
 *  a prancha tira a máquina do contrato de uma dupla e a entrega no contrato de
 *  outra, então oferecer só os contratos da dupla de quem digita esconde justamente
 *  a ponta que falta. O backend segue aplicando cliente e itens, e ignora o
 *  parâmetro para usuário de cliente. */
const GARAGE_CATEGORY = 'warehouse'
export const TM_ENDPOINT_CONTRACT = 'contract'
export const TM_ENDPOINT_GARAGE = 'garage'

export const contractOrGarageOptionsLoader: SelectOptionsLoader = async ({ query, page, pageSize, signal }) => {
  const contractsReq = http.get<Paginated<Contract>>('/contracts', {
    params: { q: query || undefined, page, per_page: pageSize, todas_duplas: 1 },
    signal,
  })
  const garagesReq = page === 1
    ? http.get<Paginated<Place>>('/places', { params: { category: GARAGE_CATEGORY, q: query || undefined, per_page: 100 }, signal })
    : Promise.resolve<Paginated<Place>>({ items: [], page: 1, pages: 1, total: 0, per_page: 100 })
  const [contracts, garages] = await Promise.all([contractsReq, garagesReq])
  const garageOpts = garages.items.map((p) => ({
    value: `${TM_ENDPOINT_GARAGE}:${p.uuid}`,
    label: p.name,
    hint: 'Garagem',
    keywords: [p.name, p.code ?? '', p.city ?? '', p.uf ?? ''],
  }))
  const contractOpts = contracts.items.map((c) => ({
    value: `${TM_ENDPOINT_CONTRACT}:${c.uuid}`,
    label: c.contract_number ?? c.name,
    hint: c.producer ?? 'Contrato',
    keywords: [c.name, c.contract_number ?? '', c.producer ?? '', c.farm?.name ?? ''],
  }))
  return {
    items: [...garageOpts, ...contractOpts],
    page: contracts.page,
    pages: contracts.pages,
    total: contracts.total + garages.total,
  }
}
